import { Avatar, Group, MultiSelect, Select, Text } from '@mantine/core';
import arrayMutators from 'final-form-arrays';
import { capitalize } from 'lodash';
import { forwardRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import Error from '../../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import { ROUTES } from '../../../routes/routes';
import { GatedSheetsConfiguration } from '../../../types/gatedContent';
import { GSheet } from '../../../types/gsheet';
import { StripePrice } from '../../../types/stripePrice';
import { formatFrequency, formatPrice } from '../../../utils/currency';
import VALIDATORS from '../../../utils/validators';
import Footer from '../../PricingPages/NewPage/Footer';
import { StripeMode } from '../../../types/pricingPage';

interface SelectItem {
    label: string;
    value: string;
    sheet: GSheet;
}

interface Props {
    stripeProducts: StripePrice[];
    sheets: GSheet[];
    configuration?: GatedSheetsConfiguration;
    onChange: (values: GatedSheetsConfiguration) => Promise<void>;
}

export default function GatedSheetsForm({ stripeProducts, sheets, configuration, onChange }: Props) {
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string>();

    const sheetsOptions: SelectItem[] = sheets.map((sheet) => ({ label: sheet.title, value: sheet.id, sheet }));
    const stripeProductOptions = stripeProducts.map((price) => ({
        label: `(${capitalize(price.livemode ? StripeMode.LIVE : StripeMode.TEST)}) ${price.product.name} ${formatPrice(
            price
        )} ${formatFrequency(price)}`,
        value: price.id,
    }));

    const handleSave = async (values: GatedSheetsConfiguration) => {
        setSubmitError(undefined);
        try {
            await onChange(values);
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };
    return (
        <>
            <Form<GatedSheetsConfiguration>
                onSubmit={onChange}
                initialValues={
                    configuration ?? {
                        gatedDomains: [],
                        gatedPaths: [],
                        gatedSheets: [{ sheet: {} as GSheet, stripePrices: [] }],
                    }
                }
                mutators={{
                    ...(arrayMutators as any),
                }}
            >
                {({ handleSubmit, invalid }) => (
                    <>
                        <StyledForm onSubmit={handleSubmit} className="w-full mt-4">
                            <FormSection>
                                <FormSectionHeader>Description</FormSectionHeader>
                                <FormSectionBody>
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Name</label>
                                        <Field name="name" type="text" validate={VALIDATORS.required}>
                                            {(props) => (
                                                <>
                                                    <input
                                                        {...props.input}
                                                        className="input w-full"
                                                        placeholder="Paid Content"
                                                    />
                                                    {props.meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={props.meta.error} />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                </FormSectionBody>
                            </FormSection>
                            <FormSection>
                                <FormSectionHeader>Content</FormSectionHeader>
                                <FormSectionBody>
                                    <FieldArray name="gatedSheets">
                                        {({ fields }) =>
                                            fields.map((name) => (
                                                <div key={name}>
                                                    <div className="mb-2">
                                                        <label className="text-sm text-gray-600">Google Sheet</label>
                                                        <Field name={`${name}.sheet`} component="input">
                                                            {({ input: { onChange, value, ...rest } }) => (
                                                                <Select
                                                                    data={sheetsOptions}
                                                                    itemComponent={ItemComponent}
                                                                    searchable
                                                                    value={value?.id || value}
                                                                    onChange={(value) => {
                                                                        onChange(sheets.find((s) => s.id === value));
                                                                    }}
                                                                    {...rest}
                                                                />
                                                            )}
                                                        </Field>
                                                    </div>
                                                    <div className="mb-2">
                                                        <label className="text-sm text-gray-600">
                                                            Required Stripe Products to access sheet
                                                        </label>
                                                        <Field name={`${name}.stripePrices`} component="input">
                                                            {({ input: { onChange, value, ...rest } }) => (
                                                                <MultiSelect
                                                                    data={stripeProductOptions}
                                                                    value={value?.map((s: StripePrice) => s.id)}
                                                                    onChange={(value) => {
                                                                        onChange(
                                                                            value.map((priceId) =>
                                                                                stripeProducts.find(
                                                                                    (s) => s.id === priceId
                                                                                )
                                                                            )
                                                                        );
                                                                    }}
                                                                    searchable
                                                                    {...rest}
                                                                />
                                                            )}
                                                        </Field>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </FieldArray>
                                </FormSectionBody>
                            </FormSection>
                            <div className="flex px-8">
                                <div className="mr-0 ml-auto">
                                    {submitError && <Error message={submitError} />}
                                    <AutoSave debounce={1000} save={handleSave} />
                                </div>
                            </div>
                        </StyledForm>
                        {configuration && (
                            <Footer
                                next={{ href: ROUTES.GATED_CONTENT_SHEETS_SNIPPET, disabled: invalid, label: 'Next' }}
                                item={configuration}
                                size="LARGE"
                            />
                        )}
                    </>
                )}
            </Form>
        </>
    );
}

const ItemComponent = forwardRef<HTMLDivElement, SelectItem>(({ value, sheet, ...others }: SelectItem, ref) => (
    <div ref={ref} {...others}>
        <Group noWrap>
            <Avatar size="xs" src={sheet.iconLink} />

            <div>
                <Text>{sheet.title}</Text>
            </div>
        </Group>
    </div>
));
